import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ShieldCheck, HardHat, Siren, ClipboardCheck, Wrench, Eye, Building2, ArrowRight, Lock } from 'lucide-react';
import { useAuth } from '../../state/AuthContext';
import type { UserRole } from '../../lib/types';
import { authApi, ApiError } from '../../lib/api';
import { Logo } from '../../components/Logo';

const DEMO_ROLES: Array<{ role: UserRole; label: string; description: string; icon: typeof ShieldCheck }> = [
  { role: 'org_admin', label: 'Organization Admin', description: 'Full tenant access: users, plants, billing.', icon: Building2 },
  { role: 'safety_officer', label: 'Safety Officer', description: 'Runs gas tests, verifies isolation, co-authorizes overrides.', icon: Siren },
  { role: 'area_authority', label: 'Area Authority', description: 'Issues permits, verifies isolation, co-authorizes overrides.', icon: ClipboardCheck },
  { role: 'performing_authority', label: 'Performing Authority', description: 'Contractor / requester role — creates permits, requests overrides.', icon: Wrench },
  { role: 'viewer', label: 'Viewer', description: 'Read-only visibility into permits and conflicts.', icon: Eye },
  { role: 'platform_admin', label: 'Platform Admin', description: 'Sensifai operator view — cross-tenant admin panel.', icon: HardHat },
];

export default function LoginPage() {
  const { demoLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPath = (location.state as { from?: string } | undefined)?.from || searchParams.get('redirect') || '/app';

  const [pending, setPending] = useState<UserRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<{ logtoConfigured: boolean; demoLoginAvailable: boolean }>({
    logtoConfigured: true,
    demoLoginAvailable: false,
  });
  const [loadingProviders, setLoadingProviders] = useState(true);

  useEffect(() => {
    if (user) {
      navigate(fromPath, { replace: true });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const authErr = params.get('authError');
    if (authErr) {
      setError(decodeURIComponent(authErr));
    }

    authApi.providers()
      .then((res) => {
        setProviders(res);
      })
      .catch(() => {
        // Fallback: assume production behavior if endpoint is not reachable
        setProviders({ logtoConfigured: true, demoLoginAvailable: false });
      })
      .finally(() => {
        setLoadingProviders(false);
      });
  }, [user, navigate, fromPath]);

  const handleEnterpriseLogin = () => {
    window.location.href = `/api/auth/login?redirect=${encodeURIComponent(fromPath)}`;
  };

  const handleDemoLogin = async (role: UserRole) => {
    setPending(role);
    setError(null);
    try {
      await demoLogin(role);
      navigate(fromPath);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Please try again.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div className="container" style={{ paddingTop: 32 }}>
        <Link to="/" className="brand" style={{ width: 'fit-content' }}>
          <Logo size={26} />
          PermitGuard AI
        </Link>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
        <div className="stack gap-6" style={{ width: '100%', maxWidth: providers.demoLoginAvailable ? 880 : 540 }}>
          
          <div className="stack gap-2" style={{ textAlign: 'center' }}>
            <span className="eyebrow" style={{ margin: '0 auto' }}>
              {providers.demoLoginAvailable ? 'Development & Evaluation' : 'Enterprise Authentication'}
            </span>
            <h1 className="h2-section">Sign in to PermitGuard AI</h1>
            <p className="body-text" style={{ maxWidth: 540, margin: '0 auto' }}>
              Authoritative process safety intelligence and conflict engine for industrial Permit-to-Work (PTW) and Lockout/Tagout (LOTO) programs.
            </p>
          </div>

          {error && (
            <div className="body-small" style={{ background: 'var(--status-critical-bg)', color: 'var(--status-critical)', borderRadius: 10, padding: '14px 18px', textAlign: 'center', border: '1px solid rgba(179,38,30,0.2)' }}>
              {error}
            </div>
          )}

          {/* Primary Enterprise SSO Section */}
          <div className="card stack gap-4" style={{ padding: '32px 28px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-elevated)' }}>
            <div className="row gap-3" style={{ alignItems: 'flex-start' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldCheck size={24} color="var(--accent)" />
              </div>
              <div className="stack gap-1">
                <div className="h3" style={{ fontSize: '1.125rem' }}>Enterprise Single Sign-On</div>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Authenticate securely using Sensifai Logto OIDC with your corporate organization credentials.
                </p>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', fontSize: '1rem', padding: '14px 20px', gap: 10 }}
              onClick={handleEnterpriseLogin}
            >
              <Lock size={18} />
              <span>Continue with Sensifai SSO</span>
              <ArrowRight size={18} />
            </button>

            <div className="row gap-2" style={{ justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '0.8125rem', marginTop: 4 }}>
              <ShieldCheck size={15} color="var(--status-clear)" />
              <span>OIDC 2.0 PKCE • MFA &amp; Role-Governed Access</span>
            </div>
          </div>

          {/* Demo Sandbox Section (Visible only when ALLOW_DEMO_LOGIN=true) */}
          {providers.demoLoginAvailable && (
            <div className="stack gap-4" style={{ marginTop: 8 }}>
              <div className="row gap-3" style={{ alignItems: 'center' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span className="body-small" style={{ color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>
                  Or explore with demo refinery roles
                </span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>

              <div className="grid-3">
                {DEMO_ROLES.map(({ role, label, description, icon: Icon }) => (
                  <button
                    key={role}
                    className="card card-interactive stack gap-2"
                    style={{ textAlign: 'left', cursor: 'pointer', border: 'none', width: '100%' }}
                    onClick={() => handleDemoLogin(role)}
                    disabled={pending !== null}
                  >
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={18} color="var(--accent)" />
                      </div>
                      {pending === role ? (
                        <span className="body-small text-tertiary">Signing in…</span>
                      ) : (
                        <ArrowRight size={14} color="var(--text-tertiary)" />
                      )}
                    </div>
                    <div className="h3" style={{ fontSize: '0.9375rem' }}>{label}</div>
                    <p className="body-small" style={{ fontSize: '0.8125rem' }}>{description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Production Notice */}
          {!loadingProviders && !providers.demoLoginAvailable && (
            <div className="body-small" style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '0 16px' }}>
              Production environment: Access is strictly managed via Sensifai SSO. Final permit authorizations, field overrides, and isolation operations require verified organizational credentials.
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
